import {
  ChatTransportError,
  type ChatMessage,
  type ChatTransport,
  type OutgoingChatMessage,
} from './chatTransport';
import { FakeChatConversation } from './fakeChatTransport';
import type { PostChatMessage } from './postInquiryMessage';

/**
 * A chat transport that derives realtime events by POLLING our own backend REST
 * API instead of connecting to Twilio's client SDK. It is the E2E realtime
 * mechanism: a spec seeds an inbound message server-side (e.g. the seed
 * builder's expert reply, a real `POST /v2/inquiries/{sid}/messages`), and this
 * transport picks it up on the next poll and fires `messageAdded` — exactly the
 * role the Twilio SDK's events play in production.
 *
 * Why polling rather than the fake's direct injection: it exercises the real
 * send -> store -> read round trip, and removes the flaky Twilio *client*
 * socket (twilsock/Sync handshake, connection races, the Sync-404 overlay). The
 * backend still reads messages from Twilio server-side, so this is a
 * reliability upgrade over the client SDK, not a Twilio-free path.
 *
 * It reuses {@link FakeChatConversation}'s paging + event emitter wholesale; the
 * only additions are a one-time hydrate and an interval that diffs a fresh fetch
 * against the cache. The transport is installed only under the test-gated seam
 * in `useTwilio`.
 *
 * The conversation class is not test-only: production builds it, through a
 * lazily loaded chunk, for the read-only inquiry staff acting as Primum Support
 * open without being a Twilio participant (see `readOnlyInquiryConversation`).
 * A change to polling, seeding or the error swallowing in `poll` reaches that
 * screen too.
 */

/** One read's scope. `after` asks only for the messages past that index. */
export type FetchMessagesOptions = { after?: number };

/** Fetches a conversation's messages from our backend: all of them, or those after an index. */
export type FetchConversationMessages = (
  conversationId: string,
  options?: FetchMessagesOptions
) => Promise<ChatMessage[]>;

const DEFAULT_POLL_INTERVAL_MS = 1000;

export class PollingChatConversation extends FakeChatConversation {
  private timer: ReturnType<typeof setInterval> | undefined;

  constructor(
    id: string,
    private readonly fetchMessages: FetchConversationMessages,
    private readonly intervalMs: number = DEFAULT_POLL_INTERVAL_MS,
    initialMessages: ChatMessage[] = [],
    /** Absent for a conversation the viewer only reads, which refuses to send. */
    private readonly post?: PostChatMessage
  ) {
    super({ id, messages: initialMessages });
  }

  /**
   * Posts through the API, then reads so the stored message arrives without
   * waiting for the next tick. The endpoint does not deduplicate, so a send
   * is not idempotent, whatever the base class answers.
   */
  override async send(message: OutgoingChatMessage): Promise<undefined> {
    if (!this.post) {
      throw new ChatTransportError('forbidden', 'This conversation is read-only');
    }

    await this.post(this.id, message);

    this.afterSend();

    return undefined;
  }

  /** What a send triggers once it lands. A subclass may read less. */
  protected afterSend(): void {
    this.poll().catch(() => undefined);
  }

  /**
   * Initial load: fill the cache WITHOUT emitting events, so the first page is
   * served through `getMessages` (the infinite query) rather than arriving as a
   * burst of `messageAdded`s before any listener is attached — mirroring how the
   * real transport delivers history via `getMessages` and only new messages via
   * events.
   */
  async hydrate(): Promise<void> {
    try {
      const remote = await this.fetchMessages(this.id);

      this.messages = [...remote].sort((first, second) => first.index - second.index);
    } catch {
      // Leave the cache empty; the poll loop will retry and backfill.
    }
  }

  start(): void {
    if (this.timer) {
      return;
    }

    this.timer = setInterval(() => {
      // `poll` swallows its own errors, so this never rejects; the catch only
      // satisfies the no-floating-promises rule without a bare `void`.
      this.tick().catch(() => undefined);
    }, this.intervalMs);
  }

  /** What each timer tick does. A subclass may skip a tick it does not need. */
  protected tick(): Promise<void> {
    return this.poll();
  }

  stop(): void {
    if (this.timer) {
      clearInterval(this.timer);

      this.timer = undefined;
    }
  }

  protected poll(): Promise<void> {
    return this.read();
  }

  /**
   * Diff a fresh fetch against the cache and replay the delta through the
   * inherited `receiveMessage`/`updateMessage`, which fire `messageAdded`/
   * `messageUpdated`. A transient fetch failure is swallowed so one bad poll
   * doesn't tear down the loop.
   *
   * The diff only adds and updates, never removes, which is what lets a read
   * after an index merge the same way as a full one, and a full list from a
   * server that ignores `after` merge as well.
   */
  protected async read(options: FetchMessagesOptions = {}): Promise<void> {
    let remote: ChatMessage[];

    try {
      remote =
        options.after === undefined
          ? await this.fetchMessages(this.id)
          : await this.fetchMessages(this.id, { after: options.after });
    } catch {
      return;
    }

    const cachedByIndex = new Map(this.messages.map((message) => [message.index, message]));

    for (const message of remote) {
      const cached = cachedByIndex.get(message.index);

      if (!cached) {
        this.receiveMessage(message);
      } else if (hasMessageChanged(cached, message)) {
        this.updateMessage(message.id, message);
      }
    }
  }
}

function hasMessageChanged(previous: ChatMessage, next: ChatMessage): boolean {
  return (
    previous.body !== next.body ||
    previous.updatedAt?.getTime() !== next.updatedAt?.getTime() ||
    JSON.stringify(previous.attributes) !== JSON.stringify(next.attributes)
  );
}

export class PollingChatTransport implements ChatTransport {
  private conversations = new Map<string, PollingChatConversation>();

  constructor(
    private readonly fetchMessages: FetchConversationMessages,
    private readonly intervalMs?: number,
    private readonly post?: PostChatMessage
  ) {}

  async getConversation(conversationId: string): Promise<PollingChatConversation> {
    const existing = this.conversations.get(conversationId);

    if (existing) {
      return existing;
    }

    const conversation = new PollingChatConversation(
      conversationId,
      this.fetchMessages,
      this.intervalMs,
      [],
      this.post
    );

    await conversation.hydrate();

    conversation.start();

    this.conversations.set(conversationId, conversation);

    return conversation;
  }

  shutdown(): Promise<void> {
    for (const conversation of this.conversations.values()) {
      conversation.stop();
    }

    this.conversations.clear();

    return Promise.resolve();
  }
}
